// Shared region search — matches by region / country / ID / zone plus climate,
// live weather, organization membership and population thresholds. Used by both
// the Ctrl+F modal and the sidebar search box so they stay in sync.

// Ranking: an ID match always outranks everything else, and a population match
// always comes last. Typing a region number used to be unusable — a bare "42"
// is also a valid "population >= 42" query, which every populated region on the
// map satisfies, and the population sort then pushed the region actually
// numbered 42 far below a wall of megacities. Hence both guards below: a bare
// number is only read as a population threshold when it is big enough to mean
// one (BARE_POP_MIN), and the results are ordered by match quality, not by
// population.
const BARE_POP_MIN = 1000

const RANK = { ID: 0, Région: 1, Ville: 2, Pays: 3, Zone: 4, Climat: 5, Météo: 6, Organisation: 7, Population: 8 }

let citiesPromise = null

export function loadCities() {
  if (!citiesPromise) {
    citiesPromise = fetch('/data/overlays/cities.geojson')
      .then((response) => {
        if (!response.ok) throw new Error('city-index-unavailable')
        return response.json()
      })
      .catch((error) => {
        citiesPromise = null
        throw error
      })
  }
  return citiesPromise
}

export function searchRegions(regionsData, rawQuery, { organizationsCatalog, weatherData, citiesData, limit = 20 } = {}) {
  const q = (rawQuery || '').trim().toLowerCase()
  if (!q) return []

  // Numeric population query: "1000000", ">1M", "<500k", ">= 2 000 000".
  const popMatch = /^([<>]=?)?\s*([\d\s.,]+)\s*([km])?$/i.exec(q)
  let popOp = null; let popThreshold = null
  if (popMatch && /\d/.test(popMatch[2])) {
    popOp = popMatch[1] || '>='
    let n = parseFloat(popMatch[2].replace(/[\s,]/g, ''))
    const suffix = (popMatch[3] || '').toLowerCase()
    if (suffix === 'k') n *= 1e3
    else if (suffix === 'm') n *= 1e6
    // A bare small number is an ID, not a population filter. An explicit
    // operator or a k/M suffix always means population, however small.
    if (popMatch[1] || suffix || n >= BARE_POP_MIN) popThreshold = n
  }
  const regionWeather = weatherData?.regions
  const out = []
  for (const f of regionsData?.features || []) {
    const p = f.properties
    const regionId = String(p.region_id || '')
    const orgNames = (p.organization_ids || [])
      .map((id) => organizationsCatalog?.[id]?.name?.toLowerCase())
      .filter(Boolean)
    const w = regionWeather?.[p.region_id] ?? regionWeather?.[String(p.region_id)]
    const weatherType = (w?.weather_type ?? p.weather_type ?? '').toLowerCase()

    let matchType = null
    if (regionId === q || regionId.includes(q)) matchType = 'ID'
    else if ((p.name || '').toLowerCase().includes(q)) matchType = 'Région'
    else if ((p.country_name || '').toLowerCase().includes(q)) matchType = 'Pays'
    else if ((p.geographical_area || '').toLowerCase().includes(q)) matchType = 'Zone'
    else if ((p.climate || '').toLowerCase().includes(q)) matchType = 'Climat'
    else if (weatherType && weatherType.includes(q)) matchType = 'Météo'
    else if (orgNames.some((n) => n.includes(q))) matchType = 'Organisation'
    else if (popThreshold != null && p.population > 0) {
      const pop = Number(p.population)
      const ok = popOp === '>' ? pop > popThreshold
        : popOp === '<' ? pop < popThreshold
        : popOp === '<=' ? pop <= popThreshold
        : pop >= popThreshold
      if (ok) matchType = 'Population'
    }

    if (matchType) out.push({ ...p, matchType })
  }

  for (const feature of citiesData?.features || []) {
    const properties = feature.properties || {}
    const cityName = (properties.name || '').toLowerCase()
    if (!cityName.includes(q)) continue
    out.push({
      name: properties.name,
      country_name: properties.country,
      population: properties.pop,
      coordinates: feature.geometry?.coordinates,
      matchType: 'Ville',
      cityMatch: cityName === q ? 0 : cityName.startsWith(q) ? 1 : 2,
    })
  }

  // Every candidate is scored, then ordered — the old early `break` at `limit`
  // could stop before ever reaching the region whose ID was typed.
  out.sort((a, b) => {
    const ra = RANK[a.matchType] ?? 99
    const rb = RANK[b.matchType] ?? 99
    if (ra !== rb) return ra - rb
    if (a.matchType === 'ID') {
      // Exact first, then the shortest ID containing the query ("4" → 4, 40, 41…).
      const ea = String(a.region_id) === q ? 0 : 1
      const eb = String(b.region_id) === q ? 0 : 1
      if (ea !== eb) return ea - eb
      const sa = String(a.region_id).indexOf(q)
      const sb = String(b.region_id).indexOf(q)
      if (sa !== sb) return sa - sb
      return String(a.region_id).length - String(b.region_id).length
    }
    if (a.matchType === 'Ville' && a.cityMatch !== b.cityMatch) return a.cityMatch - b.cityMatch
    return (b.population || 0) - (a.population || 0)
  })
  return out.slice(0, limit)
}
